/**
 * Reminder Timing Utilities
 *
 * Shared utilities for converting reminder timing values to minutes
 * and adjusting times based on reminder offsets.
 *
 * Used by:
 * - AddMedicationModal
 * - AddAppointmentModal
 * - Reminder scheduling logic
 */

import type { ReminderTimingValue } from "@src/packages/ui/ReminderTimingEditor";

/**
 * Check if a timing value is a custom timing (e.g., "custom_48m", "custom_2h").
 */
export function isCustomTiming(timing: string): timing is `custom_${string}` {
  return timing.startsWith("custom_");
}

/**
 * Parse a custom timing value into value and unit.
 * @param timing Custom timing string like "custom_48m", "custom_2h", "custom_1d"
 * @returns Parsed value and unit
 */
export function parseCustomTiming(timing: string): {
  value: number;
  unit: "m" | "h" | "d";
} {
  const match = timing.match(/^custom_(\d+)(m|h|d)$/);
  if (!match) {
    return { value: 0, unit: "m" };
  }
  return {
    value: parseInt(match[1]!, 10),
    unit: match[2] as "m" | "h" | "d",
  };
}

/**
 * Convert a timing value to minutes before the scheduled time.
 *
 * @param timing The timing value from ReminderTimingEditor
 * @returns Number of minutes before the event
 *
 * @example
 * timingToMinutesBefore("exact") // → 0
 * timingToMinutesBefore("15m_before") // → 15
 * timingToMinutesBefore("30m_before") // → 30
 * timingToMinutesBefore("1h_before") // → 60
 * timingToMinutesBefore("custom_48m") // → 48
 * timingToMinutesBefore("custom_2h") // → 120
 * timingToMinutesBefore("custom_1d") // → 1440
 */
export function timingToMinutesBefore(
  timing: ReminderTimingValue | string,
): number {
  if (isCustomTiming(timing)) {
    const { value, unit } = parseCustomTiming(timing);
    switch (unit) {
      case "m":
        return value;
      case "h":
        return value * 60;
      case "d":
        return value * 60 * 24;
    }
  }

  // Preset timings
  switch (timing) {
    case "exact":
      return 0;
    case "15m_before":
      return 15;
    case "30m_before":
      return 30;
    case "1h_before":
      return 60;
    default:
      return 0; // Fallback to exact time
  }
}

/**
 * Adjust a time string (HH:MM) by subtracting minutes.
 * Handles day wraparound (e.g., 00:30 - 60min = 23:30 previous day).
 *
 * @param timeStr Time in HH:MM format
 * @param minutesBefore Minutes to subtract
 * @returns Adjusted time in HH:MM format
 *
 * @example
 * adjustTimeByMinutes("08:00", 30) // → "07:30"
 * adjustTimeByMinutes("00:30", 60) // → "23:30" (wraps to previous day)
 */
export function adjustTimeByMinutes(
  timeStr: string,
  minutesBefore: number,
): string {
  const [hours, mins] = timeStr.split(":").map(Number);
  const totalMinutes = (hours ?? 0) * 60 + (mins ?? 0) - minutesBefore;
  // Handle negative values (wrap to previous day)
  const adjustedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
  const newHours = Math.floor(adjustedMinutes / 60);
  const newMins = adjustedMinutes % 60;
  return `${newHours.toString().padStart(2, "0")}:${newMins.toString().padStart(2, "0")}`;
}
